const loops = new Map();
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');

class DeferredLoop {
  constructor(root, trackSelector, groupSelector, speedProperty) {
    this.root = root;
    this.track = root.querySelector(trackSelector);
    this.group = root.querySelector(groupSelector);
    this.viewport = this.track?.parentElement;
    this.speedProperty = speedProperty;
    this.visible = false;
    this.hovered = false;
    this.disposed = false;
    this.events = new AbortController();
    if (!this.track || !this.group) return;
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries.some(entry => entry.isIntersecting);
      if (this.visible && !this.started) this.prepare();
      this.sync();
    }, { rootMargin: '300px 0px' });
    this.observer.observe(root);
    const options = { signal: this.events.signal };
    document.addEventListener('visibilitychange', () => this.sync(), options);
    root.addEventListener('pointerenter', event => {
      if (event.pointerType !== 'mouse') return;
      this.hovered = true;
      this.sync();
    }, options);
    root.addEventListener('pointerleave', () => {
      this.hovered = false;
      this.sync();
    }, options);
    root.addEventListener('focusin', () => this.sync(), options);
    root.addEventListener('focusout', () => queueMicrotask(() => this.sync()), options);
    reducedMotion.addEventListener('change', () => this.sync(), options);
  }

  async prepare() {
    if (this.started || this.disposed) return;
    this.started = true;
    const images = [...this.group.querySelectorAll('img')];
    await Promise.all(images.map(image => new Promise(resolve => {
      let settled = false;
      const finish = async () => {
        if (settled) return;
        settled = true;
        image.removeEventListener('load', finish);
        image.removeEventListener('error', finish);
        clearTimeout(timer);
        try { await image.decode(); } catch {}
        resolve();
      };
      const timer = setTimeout(finish, 15000);
      image.addEventListener('load', finish, { once: true });
      image.addEventListener('error', finish, { once: true });
      image.loading = 'eager';
      if (image.complete) finish();
    })));
    if (this.disposed || !this.root.isConnected) return;
    this.resize = new ResizeObserver(() => this.rebuild());
    this.resize.observe(this.viewport);
    this.rebuild();
  }

  rebuild() {
    if (this.disposed) return;
    const distance = this.group.getBoundingClientRect().width;
    const viewportWidth = this.viewport.clientWidth;
    if (!distance || !viewportWidth) return;
    const duration = Math.max(8000, (parseFloat(getComputedStyle(this.root).getPropertyValue(this.speedProperty)) || 28) * 1000);
    if (distance === this.distance && viewportWidth === this.viewportWidth && duration === this.duration) return;
    const progress = this.animation && this.duration ? (Number(this.animation.currentTime) || 0) % this.duration / this.duration : 0;
    this.animation?.cancel();
    this.track.querySelectorAll('[data-loop-copy]').forEach(copy => copy.remove());
    const copies = Math.ceil(viewportWidth / distance);
    for (let index = 0; index < copies; index++) {
      const copy = this.group.cloneNode(true);
      copy.dataset.loopCopy = '';
      copy.setAttribute('aria-hidden', 'true');
      copy.querySelectorAll('[id]').forEach(node => node.removeAttribute('id'));
      copy.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(node => node.setAttribute('tabindex', '-1'));
      this.track.append(copy);
    }
    this.distance = distance;
    this.viewportWidth = viewportWidth;
    this.duration = duration;
    this.viewport.style.overflowX = 'hidden';
    this.viewport.scrollLeft = 0;
    this.animation = this.track.animate([
      { transform: 'translateX(0)' },
      { transform: `translateX(-${distance}px)` }
    ], { duration, iterations: Infinity, easing: 'linear' });
    this.animation.currentTime = progress * duration;
    this.root.dataset.loopReady = 'true';
    this.sync();
  }

  sync() {
    if (!this.animation) return;
    this.viewport.style.overflowX = reducedMotion.matches ? 'auto' : 'hidden';
    if (this.visible && !document.hidden && !reducedMotion.matches && !this.hovered && !this.root.contains(document.activeElement)) this.animation.play();
    else this.animation.pause();
  }

  destroy() {
    this.disposed = true;
    this.events.abort();
    this.observer?.disconnect();
    this.resize?.disconnect();
    this.animation?.cancel();
  }
}

function initialize(container = document) {
  for (const [selector, track, group, speed] of [
    ['.cds-slider', '.cds-slider__track', '.cds-slider__group', '--cds-speed'],
    ['[id^="ruido-footer-gallery-"]', '.rfg-track', '.rfg-group', '--rfg-speed']
  ]) {
    container.querySelectorAll(selector).forEach(root => {
      if (!loops.has(root)) loops.set(root, new DeferredLoop(root, track, group, speed));
    });
  }
}

initialize();
document.addEventListener('shopify:section:load', event => initialize(event.target));
document.addEventListener('shopify:section:unload', event => {
  for (const [root, loop] of loops) {
    if (event.target.contains(root)) {
      loop.destroy();
      loops.delete(root);
    }
  }
});

